import React, { useState, useEffect } from 'react';
import { XIcon, RepeatIcon } from 'lucide-react';
import { useClients, type ClientRecord } from '../../hooks/useClients';

export interface ReminderFormData {
  title: string;
  description: string;
  date: string;
  time: string;
  clientId?: string;
  planId?: string | null;
}

interface EditingReminderData {
  id: string;
  title: string;
  description: string;
  due_at: string;
}

interface ReminderFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (formData: ReminderFormData) => void;
  editingReminder: EditingReminderData | null;
  clientId: string;
  isLoading: boolean;
  error: string | null;
  planId?: string | null;
  planTitle?: string | null;
}

// Helper functions
function toDateInputValue(value?: string) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  // Format as YYYY-MM-DD in local timezone
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function toTimeInputValue(value?: string) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  // Format as HH:MM in local timezone
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
}

export function ReminderFormModal({
  isOpen,
  onClose,
  onSubmit,
  editingReminder,
  clientId,
  isLoading,
  error,
  planId,
  planTitle,
}: ReminderFormModalProps) {
  const clientsQuery = useClients();
  const isEditMode = Boolean(editingReminder);

  const [formState, setFormState] = useState<ReminderFormData>({
    title: '',
    description: '',
    date: '',
    time: '',
    clientId: '',
  });

  // Populate form when editing or when clientId is provided
  useEffect(() => {
    if (editingReminder) {
      setFormState({
        title: editingReminder.title || '',
        description: editingReminder.description || '',
        date: toDateInputValue(editingReminder.due_at),
        time: toTimeInputValue(editingReminder.due_at),
        clientId: clientId,
        planId: planId,
      });
    } else {
      // New reminder - pre-fill with current date and time
      const now = new Date();
      const currentDate = toDateInputValue(now.toISOString());
      const currentTime = toTimeInputValue(now.toISOString());

      // Auto-fill title if plan context is provided
      const autoTitle = planTitle ? `Follow up: ${planTitle}` : '';

      if (clientId && clientId !== 'all') {
        // New reminder with pre-selected client
        setFormState({
          title: autoTitle,
          description: '',
          date: currentDate,
          time: currentTime,
          clientId: clientId,
          planId: planId,
        });
      } else {
        // New reminder without pre-selected client
        setFormState({
          title: autoTitle,
          description: '',
          date: currentDate,
          time: currentTime,
          clientId: '',
          planId: planId,
        });
      }
    }
  }, [editingReminder, clientId, planId, planTitle]);

  const handleSubmit = () => {
    if (!formState.clientId) {
      return;
    }
    if (!formState.title.trim()) {
      return;
    }
    if (!formState.date || !formState.time) {
      return;
    }

    onSubmit(formState);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl w-full max-w-md overflow-hidden animate-scale-in">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-[#212121]">
              {editingReminder ? 'Edit Reminder' : 'Add Reminder'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500"
            >
              <XIcon className="w-6 h-6" />
            </button>
          </div>
        </div>
        <div className="p-4 space-y-4">
          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
              {error}
            </div>
          )}
          {/* Show plan badge if linked to a plan */}
          {planId && planTitle && (
            <div className="bg-[#5B8E87] bg-opacity-10 border border-[#5B8E87] border-opacity-20 rounded-xl px-3 py-2">
              <p className="text-xs text-gray-600 mb-1">Linked to plan:</p>
              <p className="text-sm font-medium text-[#5B8E87]">{planTitle}</p>
            </div>
          )}
          {/* Only show client dropdown if no clientId is pre-selected */}
          {!clientId || clientId === 'all' ? (
            <div>
              <label className="block text-sm font-medium text-[#212121] mb-2">
                Client {!isEditMode && <span className="text-red-500">*</span>}
              </label>
              <select
                value={formState.clientId}
                onChange={(e) =>
                  setFormState((prev) => ({
                    ...prev,
                    clientId: e.target.value,
                  }))
                }
                disabled={isEditMode}
                className={`w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all ${
                  isEditMode ? 'bg-gray-100 cursor-not-allowed' : ''
                }`}
              >
                <option value="">Select a client...</option>
                {(clientsQuery.data ?? []).map((client: ClientRecord) => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </select>
              {isEditMode && (
                <p className="text-xs text-gray-500 mt-1">
                  Client cannot be changed when editing
                </p>
              )}
            </div>
          ) : null}
          <div>
            <label className="block text-sm font-medium text-[#212121] mb-2">
              Title
            </label>
            <input
              type="text"
              value={formState.title}
              onChange={(e) =>
                setFormState((prev) => ({
                  ...prev,
                  title: e.target.value,
                }))
              }
              placeholder="e.g., Weekly progress check"
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#212121] mb-2">
              Description
            </label>
            <textarea
              value={formState.description}
              onChange={(e) =>
                setFormState((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              rows={3}
              placeholder="Add context or agenda for this reminder"
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-[#212121] mb-2">
                Date
              </label>
              <input
                type="date"
                value={formState.date}
                onChange={(e) =>
                  setFormState((prev) => ({
                    ...prev,
                    date: e.target.value,
                  }))
                }
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#212121] mb-2">
                Time
              </label>
              <input
                type="time"
                value={formState.time}
                onChange={(e) =>
                  setFormState((prev) => ({
                    ...prev,
                    time: e.target.value,
                  }))
                }
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all"
              />
            </div>
          </div>
        </div>
        <div className="p-4 bg-gray-50 border-t border-gray-200 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 bg-gray-100 text-gray-600 font-semibold py-3 rounded-xl hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isLoading}
            className={`flex-1 font-semibold py-3 rounded-xl ${
              isLoading
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                : 'bg-[#5B8E87] text-white'
            }`}
          >
            {isLoading ? 'Saving...' : 'Save Reminder'}
          </button>
        </div>
      </div>
    </div>
  );
}
